// Schools-as-Venues (131) -- DugAds staff review queue for school-added venues.
// Each unverified venue can be Verified (becomes universally usable) or
// Rejected (hard-deleted, with confirmation).

import { useCallback, useEffect, useState } from "react";
import type { SchoolPublic, VenueType } from "@dugads/shared";
import * as api from "@/lib/api";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Loader2, MapPin, Trash2 } from "lucide-react";

const VENUE_TYPE_LABELS: Record<VenueType, string> = {
  school: "School",
  club: "Club",
  university: "University",
  stadium: "Stadium",
  sports_facility: "Sports facility",
};

export default function AdminPendingVenuesPage() {
  const [venues, setVenues] = useState<SchoolPublic[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmReject, setConfirmReject] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .getPendingVenues()
      .then((r) => setVenues(r.venues))
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load"));
  }, []);

  useEffect(() => load(), [load]);

  async function verify(id: string) {
    setBusyId(id);
    setError(null);
    try {
      await api.verifyVenue(id);
      setVenues((vs) => (vs ?? []).filter((v) => v.id !== id));
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Verify failed");
    } finally {
      setBusyId(null);
    }
  }

  async function reject(id: string) {
    setBusyId(id);
    setError(null);
    try {
      await api.rejectVenue(id);
      setVenues((vs) => (vs ?? []).filter((v) => v.id !== id));
      setConfirmReject(null);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Reject failed");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="mx-auto max-w-3xl p-6" data-page="admin-pending-venues" data-state={venues ? "ready" : "loading"}>
      <h1 className="mb-2 text-2xl font-semibold">Pending venues</h1>
      <p className="mb-6 text-sm text-muted-foreground">
        Venues added by school admins, awaiting DugAds review. Verifying makes a
        venue available to everyone; rejecting deletes it.
      </p>

      {error && <p className="mb-4 text-sm text-destructive" data-error>{error}</p>}

      {venues && venues.length === 0 && (
        <Card data-state="empty">
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No venues are awaiting review.
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col gap-3">
        {venues?.map((v) => (
          <Card key={v.id} data-item="pending-venue" data-item-id={v.id}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                {v.name}
                <Badge variant="secondary">{VENUE_TYPE_LABELS[v.venue_type]}</Badge>
              </CardTitle>
              <CardDescription>
                {[v.town, v.province].filter(Boolean).join(", ") || v.location || "No location"}
                {v.google_place_id ? ` · place: ${v.google_place_id}` : ""}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => verify(v.id)}
                disabled={busyId === v.id}
                data-action="verify"
              >
                {busyId === v.id ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : (
                  <Check className="mr-1 h-4 w-4" />
                )}
                Verify
              </Button>
              {confirmReject === v.id ? (
                <>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => reject(v.id)}
                    disabled={busyId === v.id}
                    data-action="confirm-reject"
                  >
                    Confirm delete
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmReject(null)}>
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-destructive"
                  onClick={() => setConfirmReject(v.id)}
                  data-action="reject"
                >
                  <Trash2 className="mr-1 h-4 w-4" />
                  Reject
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
